import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { Close } from "@material-ui/icons";
import {
  getPracticeClaimCPTRecommendationDetails,
  getPracticeClaimICDRecommendationDetails,
  updatePracticeClaimCPTRecommendationDetails,
  updatePracticeClaimICDRecommendationDetails,
} from "apiHandler/actions";

import { cptRightColumns, icdRightColumns } from "../claimDetails/helper";
import NoteDialog from "../claimDetails/noteDialog";
import CodeTable from "../claimDetails/codeTable";
import ClaimPatientInfo from "../claimDetails/claimPatientInfo";

const collapseSections = [
  "clinical_justification",
  "supporting_evidence",
  "documentation_requirements",
  "user_decision_notes",
];

const EncounterDetails = ({ open, onClose, claim, section, userData }) => {
  const [aiRows, setAiRows] = useState([]);
  const [aiPriRows, setAiPriRows] = useState([]);
  const [aiSecRows, setAiSecRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [note, setNote] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isIcd, setIsIcd] = useState(false);
  const token = userData?.access_token;

  useEffect(() => {
    if (open && claim) {
      getClaimDetails();
    }
  }, [open, claim?.claim_id, section]);

  const getClaimDetails = async () => {
    setLoading(true);
    setAiRows([]);
    setAiPriRows([]);
    setAiSecRows([]);
    try {
      const payload = {
        practice_id: userData?.selected_practice,
        claim_no: `${claim?.claim_no}`,
      };

      const resIcd = await getPracticeClaimICDRecommendationDetails(
        payload,
        token
      );

      const resCpt = await getPracticeClaimCPTRecommendationDetails(
        payload,
        token
      );

      const aiPrimaryCodes = resCpt.filter((row) => Number(row.rvu_value) > 0);

      const aiQualityCodes = resCpt.filter(
        (row) => Number(row.rvu_value) === 0
      );

      setAiRows(resIcd);

      setAiPriRows(aiPrimaryCodes);

      setAiSecRows(aiQualityCodes);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (row, action, section) => {
    setAction(action);
    setSelectedRow(row);
    setOpenSubmit(true);
    setIsIcd(section === "ICD");
  };

  const closeSubmit = () => {
    setOpenSubmit(false);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        practice_id: userData?.selected_practice,
        recommendation_id: action,
        user_decision: selectedRow?.user_decision,

        user_name: userData.first_name + " " + userData.last_name,
        ...(note !== "" && { user_decision_notes: note }),
      };
      let res;
      if (isIcd) {
        res = await updatePracticeClaimICDRecommendationDetails(payload, token);
      } else {
        res = await updatePracticeClaimCPTRecommendationDetails(payload, token);
      }
      if (res && res?.status === "successfull") {
        setSnackbarSeverity("success");
        setSnackbarMessage("Submission successful");
        setSnackbarOpen(true);
        getClaimDetails();
      }

      closeSubmit();
    } catch (error) {
      console.log("error: ", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Submission failed. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      onClose={onClose}
      aria-labelledby="claim-detail-title"
    >
      <DialogContent>
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          justifyContent="space-between"
        >
          <Typography variant="h6" id="claim-detail-title">
            Encounter Detail ({claim?.claim_no})
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <ClaimPatientInfo
          practiceID={userData?.selected_practice}
          token={token}
          claim={claim}
          showICD={true}
          showCPT={true}
          setSnackbarSeverity={setSnackbarSeverity}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarOpen={setSnackbarOpen}
          showOriginal={false}
        />

        <CodeTable
          title="ICD AI Codes"
          rows={aiRows}
          columns={icdRightColumns(handleUpdate)}
          loading={loading}
          collapseSections={collapseSections}
        />

        <CodeTable
          title="CPT AI Primary Codes"
          rows={aiPriRows}
          columns={cptRightColumns(handleUpdate)}
          loading={loading}
          collapseSections={collapseSections}
        />

        <CodeTable
          title="CPT AI Quality Codes"
          rows={aiSecRows}
          columns={cptRightColumns(handleUpdate)}
          loading={loading}
          collapseSections={collapseSections}
        />

        <NoteDialog
          open={openSubmit}
          onClose={closeSubmit}
          note={note}
          setNote={setNote}
          onSubmit={onSubmit}
          submitting={submitting}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default EncounterDetails;
